import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    type CreateDealerMutation,
    type CreateDealerMutationVariables,
    CreateDealerDocument,
} from '../../../api/mutations/createDealer';
import {
    type CreateDealerSocialMediaMutation,
    type CreateDealerSocialMediaMutationVariables,
    CreateDealerSocialMediaDocument,
} from '../../../api/mutations/createDealerSocialMedia';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import useValidator from '../../../utilities/useValidator';
import DealerForm, { defaultValue, type FormValues } from '../DealerDetailsPage/sections';
import dealerValidatorForm from '../DealerDetailsPage/useDealerValidators';
import useHandleDealerAsset from '../DealerDetailsPage/useHandleDealerAsset';

const AddDealerPage = () => {
    const { t } = useTranslation('dealerForm');
    const validate = useValidator(dealerValidatorForm);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const handleDealerAsset = useHandleDealerAsset();
    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { companyId, dealerInput, socialMedia } = values;
            setLoading(true);
            message.loading({
                content: t('dealerForm:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });
            const { data } = await apolloClient.mutate<CreateDealerMutation, CreateDealerMutationVariables>({
                mutation: CreateDealerDocument,
                variables: {
                    companyId,
                    dealerInput,
                },
            });

            const promises = (socialMedia || []).map(async media => {
                const mediaInput = omit(media, 'icon');

                return apolloClient
                    .mutate<CreateDealerSocialMediaMutation, CreateDealerSocialMediaMutationVariables>({
                        mutation: CreateDealerSocialMediaDocument,
                        variables: {
                            dealerId: data.createDealer.id,
                            dealerSocialMediaInput: mediaInput,
                        },
                    })
                    .then(createMedia => handleDealerAsset(createMedia.data.createDealerSocialMedia.id, media.icon));
            });

            await Promise.all(promises);

            message.success({
                content: t('dealerForm:messages.createSuccessful'),
                key: 'primary',
            });
            navigate(`/admin/dealers`);
        },
        [apolloClient, handleDealerAsset, navigate, setLoading, t],
        undefined,
        () => {
            setLoading(false);
        }
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="addDealerForm" htmlType="submit" type="primary">
                    {t('dealerForm:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/dealers')}
            title={t('dealerForm:addTitle')}
        >
            <Formik initialValues={defaultValue} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="addDealerForm" name="addDealerForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <DealerForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddDealerPage;
